import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Select, Button, Switch, message, Tag, Row, Col, Alert, Popconfirm, Spin } from 'antd';
import { ref, get, set, push, remove, onValue } from 'firebase/database';
import { useFirebase } from '../FirebaseContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  ShieldCheck,
  ArrowsClockwise,
  Plugs,
  Envelope,
  UserPlus,
  Trash,
  FloppyDisk,
  Clock,
} from '@phosphor-icons/react';
import dayjs from 'dayjs';
import './KsefSettings.css';

const KsefSettings = () => {
  const { currentFirebase, currentProject } = useFirebase();
  const db = currentFirebase.database;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [syncStats, setSyncStats] = useState({ total: 0, lastSync: null });
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const intervalRef = useRef(null);
  const [accessEmails, setAccessEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    const accessRef = ref(db, 'ksefAccessEmails');
    const unsub = onValue(accessRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setAccessEmails(Object.entries(data).map(([key, email]) => ({ key, email })));
      } else {
        setAccessEmails([]);
      }
    });
    return () => unsub();
  }, [db]);

  const handleAddEmail = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return message.warning('Wprowadź poprawny email');
    if (accessEmails.some(e => e.email.toLowerCase() === email)) return message.warning('Email już ma dostęp');
    try {
      await push(ref(db, 'ksefAccessEmails'), email);
      setNewEmail('');
      message.success(`Dostęp nadany: ${email}`);
    } catch (err) { message.error('Błąd: ' + err.message); }
  };

  const handleRemoveEmail = async (key) => {
    try { await remove(ref(db, `ksefAccessEmails/${key}`)); message.success('Usunięto'); }
    catch (err) { message.error('Błąd: ' + err.message); }
  };

  useEffect(() => {
    setPageLoading(true);
    Promise.all([loadSettings(), loadSyncHistory()]).finally(() => setPageLoading(false));
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [currentProject]);

  const loadSettings = async () => {
    try {
      const snap = await get(ref(db, 'ksefSettings'));
      if (snap.exists()) {
        const data = snap.val();
        setSettings(data);
        form.setFieldsValue(data);
        setAutoSyncEnabled(data.autoSync || false);
        if (data.autoSync) startAutoSync(data);
      }
    } catch (err) { console.error('Load settings error:', err); }
  };

  const loadSyncHistory = async () => {
    try {
      const snap = await get(ref(db, 'ksefSyncHistory'));
      if (snap.exists()) {
        const list = Object.entries(snap.val())
          .map(([key, val]) => ({ key, ...val }))
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          .slice(0, 15);
        setSyncHistory(list);
        const totalSnap = await get(ref(db, 'ksefInvoices'));
        setSyncStats({
          total: totalSnap.exists() ? Object.keys(totalSnap.val()).length : 0,
          lastSync: list[0]?.timestamp || null,
        });
      }
    } catch (err) { console.error('Load sync history error:', err); }
  };

  const handleSave = async (values) => {
    setLoading(true);
    try {
      await set(ref(db, 'ksefSettings'), { ...values, autoSync: autoSyncEnabled, updatedAt: new Date().toISOString() });
      setSettings(values);
      message.success('Ustawienia zapisane');
    } catch (err) { message.error('Błąd: ' + err.message); }
    setLoading(false);
  };

  const handleTestConnection = async () => {
    const values = form.getFieldsValue();
    if (!values.nip || !values.token) return message.warning('Wprowadź NIP i token');
    setTestLoading(true);
    try {
      const functions = getFunctions(currentFirebase.app, 'europe-west1');
      await httpsCallable(functions, 'ksefSync')({
        nip: values.nip, token: values.token,
        environment: values.environment || 'test',
        dateFrom: dayjs().subtract(1, 'day').toISOString(),
        dateTo: dayjs().toISOString(),
      });
      message.success('Połączenie OK!');
    } catch (err) { message.error('Błąd: ' + (err.message || err)); }
    setTestLoading(false);
  };

  const handleSync = async () => {
    const values = form.getFieldsValue();
    if (!values.nip || !values.token) return message.warning('Wprowadź NIP i token');
    setSyncLoading(true);
    try {
      const functions = getFunctions(currentFirebase.app, 'europe-west1');
      const result = await httpsCallable(functions, 'ksefSync')({
        nip: values.nip, token: values.token,
        environment: values.environment || 'test',
        dateFrom: dayjs().subtract(30, 'day').toISOString(),
        dateTo: dayjs().toISOString(),
      });
      message.success(`Sync: ${result.data.fetched} faktur, ${result.data.newInvoices} nowych`);
      loadSyncHistory();
    } catch (err) { message.error('Błąd: ' + (err.message || err)); }
    setSyncLoading(false);
  };

  const startAutoSync = (cfg) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => handleSync(), (cfg?.syncIntervalMinutes || 15) * 60 * 1000);
  };

  const toggleAutoSync = (checked) => {
    setAutoSyncEnabled(checked);
    if (checked) { startAutoSync(form.getFieldsValue()); message.info('Auto-sync ON'); }
    else { if (intervalRef.current) clearInterval(intervalRef.current); message.info('Auto-sync OFF'); }
  };

  if (pageLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  return (
    <div className="ksef-page">
      <Row gutter={16}>
        {/* Left: Config */}
        <Col xs={24} lg={14}>
          <div className="ksef-card">
            <div className="ksef-card-header">
              <ShieldCheck size={18} weight="duotone" color="#2563eb" />
              <span className="ksef-card-title">Konfiguracja</span>
              <Tag color={settings?.nip ? 'green' : 'default'} style={{ marginLeft: 'auto' }}>
                {settings?.nip ? 'OK' : 'Brak'}
              </Tag>
            </div>
            <div className="ksef-card-body">
              <Form form={form} layout="vertical" onFinish={handleSave} initialValues={{ environment: 'test', syncIntervalMinutes: 15 }} className="ksef-form">
                <Form.Item name="nip" label="NIP firmy" rules={[{ required: true, message: 'NIP' }]}>
                  <Input placeholder="1234567890" maxLength={10} />
                </Form.Item>
                <Form.Item name="token" label="Token KSeF" rules={[{ required: true, message: 'Token' }]} extra="Z portalu ksef.mf.gov.pl">
                  <Input.Password placeholder="Token" />
                </Form.Item>
                <div className="ksef-form-row">
                  <Form.Item name="environment" label="Środowisko" style={{ flex: 1 }}>
                    <Select>
                      <Select.Option value="test">TEST</Select.Option>
                      <Select.Option value="demo">DEMO</Select.Option>
                      <Select.Option value="production">PRODUKCJA</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="syncIntervalMinutes" label="Interwał sync" style={{ flex: 1 }}>
                    <Select>
                      <Select.Option value={5}>5 min</Select.Option>
                      <Select.Option value={15}>15 min</Select.Option>
                      <Select.Option value={30}>30 min</Select.Option>
                      <Select.Option value={60}>1 godz.</Select.Option>
                    </Select>
                  </Form.Item>
                </div>
                <Form.Item label="Auto-synchronizacja">
                  <Switch checked={autoSyncEnabled} onChange={toggleAutoSync} checkedChildren="ON" unCheckedChildren="OFF" />
                </Form.Item>
                <div className="ksef-form-actions">
                  <Button type="primary" htmlType="submit" loading={loading}>
                    <FloppyDisk size={15} weight="bold" /> Zapisz
                  </Button>
                  <Button onClick={handleTestConnection} loading={testLoading}>
                    <Plugs size={15} weight="bold" /> Test
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </Col>

        {/* Right: Sync + History */}
        <Col xs={24} lg={10}>
          <div className="ksef-card" style={{ marginBottom: 14 }}>
            <div className="ksef-card-header">
              <ArrowsClockwise size={18} weight="duotone" color="#16a34a" />
              <span className="ksef-card-title">Synchronizacja</span>
            </div>
            <div className="ksef-card-body">
              <div className="ksef-stats-row">
                <div className="ksef-stat">
                  <span className="ksef-stat-value">{syncStats.total}</span>
                  <span className="ksef-stat-label">Faktur KSeF</span>
                </div>
                <div className="ksef-stat">
                  <span className="ksef-stat-value">{syncStats.lastSync ? dayjs(syncStats.lastSync).format('DD.MM HH:mm') : '—'}</span>
                  <span className="ksef-stat-label">Ostatni sync</span>
                </div>
              </div>
              <Button type="primary" block loading={syncLoading} onClick={handleSync} style={{ marginTop: 12 }}>
                <ArrowsClockwise size={15} weight="bold" /> Synchronizuj teraz
              </Button>
              <Alert type="info" showIcon message="Pobierze faktury z ostatnich 30 dni" style={{ marginTop: 10, borderRadius: 8 }} />
            </div>
          </div>

          <div className="ksef-card">
            <div className="ksef-card-header">
              <Clock size={18} weight="duotone" color="#6b7280" />
              <span className="ksef-card-title">Historia</span>
            </div>
            <div className="ksef-history-list">
              {syncHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af', fontSize: 13 }}>Brak historii</div>
              ) : syncHistory.slice(0, 8).map((item) => (
                <div key={item.key} className="ksef-history-item">
                  <span className={`ksef-history-dot ${item.errors > 0 ? 'error' : 'ok'}`} />
                  <div className="ksef-history-info">
                    <span className="ksef-history-date">{dayjs(item.timestamp).format('DD.MM.YYYY HH:mm')}</span>
                    <span className="ksef-history-meta">
                      {item.fetched} pobrano, {item.newInvoices} nowych
                      {item.errors > 0 && <span style={{ color: '#dc2626' }}>, {item.errors} błędów</span>}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Col>
      </Row>

      {/* Access */}
      <div className="ksef-card" style={{ marginTop: 14 }}>
        <div className="ksef-card-header">
          <Envelope size={18} weight="duotone" color="#7c3aed" />
          <span className="ksef-card-title">Dostęp do KSeF</span>
          <Tag color="blue" style={{ marginLeft: 'auto' }}>{accessEmails.length}</Tag>
        </div>
        <div className="ksef-card-body">
          <div className="ksef-email-input">
            <Input
              prefix={<Envelope size={15} color="#9ca3af" />}
              placeholder="Email użytkownika"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onPressEnter={handleAddEmail}
              style={{ flex: 1 }}
            />
            <Button type="primary" onClick={handleAddEmail}>
              <UserPlus size={15} weight="bold" /> Dodaj
            </Button>
          </div>
          <div className="ksef-email-list">
            {accessEmails.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 16, color: '#9ca3af', fontSize: 13 }}>Brak</div>
            ) : accessEmails.map((item) => (
              <div key={item.key} className="ksef-email-item">
                <Envelope size={15} color="#2563eb" />
                <span className="ksef-email-text">{item.email}</span>
                <Popconfirm title="Usunąć dostęp?" onConfirm={() => handleRemoveEmail(item.key)} okText="Tak" cancelText="Nie">
                  <button className="ksef-email-del"><Trash size={14} /></button>
                </Popconfirm>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KsefSettings;
